//TODO utils配下でよいか検討し、必要なら別フォルダに移動

import { BillingCycle, ContractPlan } from '@crew/enums/app'
import { JsonDateFormat } from '@crew/enums/system'
import { KeywordFilterCondition } from '@crew/enums/domain'
import dayjs from '@crew/modules'
import { getEnumDataByValue } from '@crew/utils/enum'
import { isTaskExpired } from '@crew/utils/task'
import { TaskPriorities } from 'enums/app'
import { CrewBadgeInvertedColorClass } from 'enums/color'
import { isEqual, isNil, omitBy } from 'lodash'
import { ParsedQs } from 'qs'
import { Event } from '@crew/apis/event/models/getEvents/response'

import { memo } from 'react'
import { Row } from '@tanstack/react-table'
import { TaskStateRef } from '@crew/models/refs'

// 文字からバッジ色(inverted)を取得する
export const convertStringToBadgeColor = (str: string) => {
  //バッジの色リスト
  const colors = Object.values(CrewBadgeInvertedColorClass)

  const initials = str.trim()[0].charAt(0) // get first letter

  const charIndex = initials.charCodeAt(0)

  const colorIndex = charIndex % colors.length // get index of colour
  return colors[colorIndex]
}

// genericsコンポーネント向けに型を素通しするmemo関数
// https://stackoverflow.com/questions/57477395/typescript-generic-class-equivalent-for-react-memo
//TODO: webとmobileでReactのバージョンが違うため共通化できない。CREW-1322 の対応でReact18に揃ったら@crew/utilsに移動する。
export const genericMemo: <T>(component: T) => T = memo

export const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window
  return {
    width,
    height,
  }
}

// タスク優先度の値からEnumを取得して返す関数
export const getTaskPriorityEnumByValue = (value: number) => {
  return getEnumDataByValue(TaskPriorities, value)
}

// set covert file to base64
export const convertFileToBase64 = (file: File) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()
    fileReader.readAsDataURL(file)

    fileReader.onload = () => {
      resolve(fileReader.result)
    }

    fileReader.onerror = (error) => {
      reject(error)
    }
  })
}

// validate value is ContractPlan value
export const validateContractPlan = (value: unknown): value is ContractPlan => {
  if (typeof value !== 'string' || value === null) return false
  return Object.values(ContractPlan).includes(value as ContractPlan)
}

// validate value is BillingCycle value
export const validateBillingCycle = (value: unknown): value is BillingCycle => {
  if (typeof value !== 'string' || value === null) return false
  return Object.values(BillingCycle).includes(value as BillingCycle)
}

export const isEqualParams = (a: object, b: object): boolean => {
  const aWithoutUndefined = omitBy(a, isNil)
  const bWithoutUndefined = omitBy(b, isNil)

  return isEqual(aWithoutUndefined, bWithoutUndefined)
}

export function getParamAsString(
  key: string,
  parsed: ParsedQs
): string | undefined {
  const value = parsed[key]

  if (typeof value === 'string') {
    return value
  }

  return undefined
}

export function getParamAsArray(
  key: string,
  parsed: ParsedQs
): string[] | undefined {
  const value = parsed[key]

  // ひとつの場合も配列で返す
  if (typeof value === 'string' && value.length > 0) {
    return [value]
  } else if (Array.isArray(value)) {
    return value.map(String) // string[] に変換
  }

  return undefined
}

export function getUniqueStr(myStrong?: number): string {
  let strong = 1000
  if (myStrong) strong = myStrong
  return (
    new Date().getTime().toString(16) +
    Math.floor(strong * Math.random()).toString(16)
  )
}

// Check valid date string (format: yyyy-MM-dd)
export function isValidDate(value: string | undefined): boolean {
  return /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/.test(value ?? '')
}

export function getParamAsDate(
  key: string,
  parsed: ParsedQs
): string | undefined {
  const value = parsed[key]

  if (typeof value === 'string') {
    const dateArray = value.split(':')
    const dateValue = dateArray.length > 1 ? dateArray[1] : undefined
    switch (dateArray[0]) {
      case 'yesterday':
      case 'today':
      case 'tomorrow':
      case 'lastMonth':
      case 'thisMonth':
      case 'nextMonth':
        return value
      case 'thatDay':
      case 'beforeAt':
      case 'afterAt':
        if (isValidDate(dateValue)) {
          return value
        }
        return undefined
      case 'pastDays':
        if (!isNaN(Number(dateValue))) {
          return value
        }
        return undefined
      case 'between':
        const dateValueArray = dateValue ? dateValue.split('_') : []
        if (dateValueArray.length === 2) {
          return isValidDate(dateValueArray[0]) &&
            isValidDate(dateValueArray[1])
            ? value
            : undefined
        }
        return undefined
      case 'nextDays':
        const valueArray = dateValue ? dateValue.split('_') : []
        if (valueArray.length === 2) {
          return !isNaN(Number(valueArray[0])) &&
            typeof valueArray[1] === 'string'
            ? value
            : undefined
        }
        return undefined
    }
  }

  return undefined
}

// これまで行っていたwindow.location.hrefに代入する処理だと都合が悪い（画面遷移として扱われてしまう）
// Function to download a file from a given URL
/**
 * 引数のURLからファイルをダウンロードする
 * @param url ダウンロード先URL
 * @param fileName ダウンロード時に設定するファイル名
 * @param useCache キャッシュを使用するかどうか
 */
export const downloadFileFromUrl = async (
  url: string,
  fileName: string,
  useCache: boolean = true
) => {
  // 引数のURLからファイルをダウンロードする
  // useCacheが指定されていない場合はキャッシュを使用し、falseの場合はキャッシュを使用しないようにする
  // これは、ChromeのCORSポリシーに関連しており、キャッシュされているとOriginヘッダーが付与されないため、S3側のCORS設定によってエラーが発生する。
  // https://serverfault.com/questions/856904/chrome-s3-cloudfront-no-access-control-allow-origin-header-on-initial-xhr-req/856948#856948
  // この問題はLightboxのファイルダウンロード処理に影響があったため、CREW-14564で対応を行った
  // （Lightbox上に表示するURLとダウンロード時のURLが同じになることに起因している）
  const response = await fetch(url, {
    cache: useCache ? 'default' : 'no-store',
  })

  // Convert the response into a Blob (Binary Large OBject)
  // which represents data that isn't necessarily in a JavaScript-native format
  const blob = await response.blob()

  // Create a new object URL representing the specified File object or Blob object
  const downloadUrl = window.URL.createObjectURL(blob)

  // Create a new 'a' HTML element
  const a = document.createElement('a')

  // Set the href of the 'a' element to the download URL
  a.href = downloadUrl

  // Set the download attribute of the 'a' element to the provided file name
  // This will be the file name for the downloaded file
  a.download = fileName

  // Programmatically click the 'a' element to start the download
  a.click()

  // After triggering the download, clean up the object URL to free up memory
  window.URL.revokeObjectURL(downloadUrl)
}

// Custom sorting function for WBS (Work Breakdown Structure) numbers.
export const compareWBSNumber = (a: string, b: string): number => {
  // Regular expression to match valid WBS format x.x.x
  const wbsRegex = /^\d+(\.\d+)*$/

  // Function to split WBS numbers into an array of numbers
  const parseWBS = (wbs: string): number[] => {
    return wbs.split('.').map(Number)
  }

  // Check if both WBS numbers are valid
  const aIsValid = wbsRegex.test(a)
  const bIsValid = wbsRegex.test(b)

  // If both are valid, compare them numerically
  if (aIsValid && bIsValid) {
    const aParts = parseWBS(a)
    const bParts = parseWBS(b)

    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aValue = aParts[i] ?? 0
      const bValue = bParts[i] ?? 0

      if (aValue < bValue) return -1
      if (aValue > bValue) return 1
    }
    return 0
  }

  // If only one is valid, the valid one should come first
  if (aIsValid) return -1
  if (bIsValid) return 1

  // If a is empty, it should come first
  if (!a) return 1

  // If both are invalid, compare them lexicographically
  return a.localeCompare(b)
}

// Get the operator for keyword filter
export const getParamsOperator = (
  key: string,
  parsed: ParsedQs
): KeywordFilterCondition | undefined => {
  const value = parsed[key]

  if (value === 'and' || value === 'or') {
    return value
  }

  return undefined
}

// Function to sort an array of events by their start and end times
export const sortByStartTimeAndEndTime = (data: Event[]) => {
  // Sorts the data array in place
  data.sort((a, b) => {
    // sort by date first
    const dateA = dayjs(a.startDatetime).format(JsonDateFormat.YYYYMMDD)
    const dateB = dayjs(b.startDatetime).format(JsonDateFormat.YYYYMMDD)
    if (dateA < dateB) {
      return 1
    } else if (dateA > dateB) {
      return -1
    } else {
      const startDateTimeAFormatted = dayjs(a.startDatetime).format(
        JsonDateFormat.YYYYMMDDHHmm
      )
      const startDateTimeBFormatted = dayjs(b.startDatetime).format(
        JsonDateFormat.YYYYMMDDHHmm
      )
      const endDateTimeAFormatted = dayjs(a.endDatetime).format(
        JsonDateFormat.YYYYMMDDHHmm
      )
      const endDateTimeBFormatted = dayjs(b.endDatetime).format(
        JsonDateFormat.YYYYMMDDHHmm
      )
      // sort by startDatetime when date is same
      if (startDateTimeAFormatted < startDateTimeBFormatted) {
        return 1
      } else if (startDateTimeAFormatted > startDateTimeBFormatted) {
        return -1
      } else {
        // If the start times are equal, sort by end time
        if (endDateTimeAFormatted < endDateTimeBFormatted) {
          return 1
        } else if (endDateTimeAFormatted > endDateTimeBFormatted) {
          return -1
        } else {
          return 0
        }
      }
    }
  })
  return data
}

// group by startDatetime
// Ex: { '2022-01': [event1, event2], '2022-02': [event3, event4] }
export const createEventsGroupMap = (events: Event[]) => {
  // Reduce the events array into a map where each key is a start date and each value is an array of events starting on that date
  const newEvents = events.reduce((acc, event) => {
    // Format the event's start date as a key
    const key = dayjs(event.startDatetime).format(JsonDateFormat.YYYYMM)
    if (!acc[key]) {
      acc[key] = [] // Initialize the array if this is the first event for this date
    }
    acc[key].push(event) // Add the event to the array for its start date

    acc[key] = sortByStartTimeAndEndTime(acc[key]) // Sort the events array for this date

    return acc // Return the updated accumulator
  }, {} as Record<string, Event[]>)

  return newEvents
}

/**
 * 指定されたファイルのファイル名にタイムスタンプの挿入が必要かどうかを判定する。
 * 添付ボタンから直接カメラを起動して撮影した場合や、ビットマップをペーストした場合が該当する
 * @param file
 * @returns
 */
export const shouldModifyFileNameForUpload = (file: File): boolean => {
  /* 判定は、以下の方法で行う。
   *  1. 名前がパターンに合致するか
   *  2. 作成時刻が10秒以内か
   *
   * 1. 名前がパターンに合致するか
   *  直接カメラで撮影した場合やビットマップをペーストした場合、特定のファイル名になる事が判っているため、
   *  それらのファイル名パターンを条件とする。
   *
   * 2. 作成時刻が10秒以内か
   *  直接カメラで撮影した場合は「この画像を使う」ボタンを押した時点で、
   *  ビットマップをペーストした場合はその時点でのタイムスタンプが、
   *  Fileオブジェクトに格納されることが判っている。
   *  実測では、カメラ撮影後に「使う」ボタンを押してから最大1秒程度の時間でこの処理が走ることがわかっているため、
   *  余裕を持って10秒以内であることを条件とする。
   *
   * なお、1の条件に合致するファイル名を編集していて、保存して10秒以内にCrewに添付するパターンがあり得るが、
   * 非常にレアケースであること、万一発生しても自動的に更新扱いにならないという症状が発生するだけであるため許容する。
   */

  // 名前がパターンに合致するか
  const filename = file.name.toLowerCase()
  if (!/^(img|image)\.(jpg|jpeg|png)$/.test(filename)) {
    return false
  }

  // 作成時刻が10秒以内か
  const currentTime = new Date().getTime()
  const fileTime = file.lastModified
  if (currentTime - fileTime > 10_000) {
    return false
  }

  // 全ての条件を満たしていればtrue
  return true
}

// タスクテーブルにおいて、期限切れのタスクのためのクラス名を返す
export const getExpiredTaskRowClassName = <
  T extends { dueDate: string | null; taskState: TaskStateRef }
>(
  row: Row<T>
) => {
  const isOverdue = isTaskExpired(row.original.dueDate, row.original.taskState)

  return isOverdue
    ? {
        group: 'bg-crew-red-200 dark:bg-crew-red-900',
        groupHover:
          'group-hover:bg-crew-red-300 dark:group-hover:bg-crew-red-800',
      }
    : {}
}
